import React, { useCallback } from 'react';
import { Button } from '@blueprintjs/core';
import { PaginationButtonContainer } from './styles';
import { range } from '../../../../utils';

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (pageNumber: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  onPageChange,
}) => {
  const paginate = useCallback(
    (page: number) => onPageChange(page),
    [onPageChange],
  );

  const renderPageButtons = useCallback(() => {
    if (totalPages <= 7) {
      return range(1, totalPages + 1).map((page) => (
        <Button
          key={page}
          onClick={() => paginate(page)}
          disabled={currentPage === page}
        >
          {page}
        </Button>
      ));
    }

    const firstPageRange = Math.max(1, currentPage - 2);
    const lastPageRange = Math.min(totalPages, firstPageRange + 4);

    const buttons = [];

    if (firstPageRange > 1) {
      buttons.push(
        <Button
          key={1}
          onClick={() => paginate(1)}
          disabled={currentPage === 1}
        >
          1
        </Button>,
      );
    }

    if (firstPageRange > 2) {
      buttons.push(
        <Button disabled minimal key={'first-ellipsis'}>
          ...
        </Button>,
      );
    }

    buttons.push(
      ...range(firstPageRange, lastPageRange + 1).map((page) => (
        <Button
          key={page}
          onClick={() => paginate(page)}
          disabled={currentPage === page}
        >
          {page}
        </Button>
      )),
    );

    if (lastPageRange < totalPages - 1) {
      buttons.push(
        <Button disabled minimal key={'last-ellipsis'}>
          ...
        </Button>,
      );
    }

    if (lastPageRange < totalPages) {
      buttons.push(
        <Button
          key={totalPages}
          onClick={() => paginate(totalPages)}
          disabled={currentPage === totalPages}
        >
          {totalPages}
        </Button>,
      );
    }

    return buttons;
  }, [currentPage, paginate, totalPages]);

  return (
    <PaginationButtonContainer>
      <div>
        <Button
          key={'to-first-page'}
          data-testid={'to-first-page'}
          onClick={() => paginate(1)}
          disabled={totalPages === 1 || currentPage === 1}
          icon={'double-chevron-left'}
        />
        <Button
          key={'previous-page'}
          data-testid={'previous-page'}
          onClick={() => paginate(currentPage - 1)}
          disabled={totalPages === 1 || currentPage === 1}
          icon={'chevron-left'}
        />
        {renderPageButtons()}
        <Button
          key={'next-page'}
          data-testid={'next-page'}
          onClick={() => paginate(currentPage + 1)}
          disabled={totalPages === 1 || currentPage === totalPages}
          icon={'chevron-right'}
        />
        <Button
          key={'to-last-page'}
          data-testid={'to-last-page'}
          onClick={() => paginate(totalPages)}
          disabled={totalPages === 1 || currentPage === totalPages}
          icon={'double-chevron-right'}
        />
      </div>
    </PaginationButtonContainer>
  );
};

export default Pagination;
